import { memo, useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { Input, Result, Typography } from 'antd'
import { ThreeDot } from 'react-loading-indicators'
import { EnhancementDescriptionResponse } from 'models/product'
import { productApi } from 'api/product'
import { useSocketRequest } from 'shared/hooks/useSocketRequest'
import { AiBadge, OrangeButton, RegenerateAiButton, VariantButton } from 'components/ui'
import styles from './AiProductDescription.module.scss'

interface AiProductDescriptionProps {
  productId: string
  initialDescriptions?: EnhancementDescriptionResponse[]
  type: 'create' | 'edit',
  additionalPrompt?: string
  selectedDescription: EnhancementDescriptionResponse | null
  setSelectedDescription: React.Dispatch<React.SetStateAction<EnhancementDescriptionResponse | null>>
  setItemId: (id: string) => void
}

export const AiProductDescription: React.FC<AiProductDescriptionProps> = memo(({ productId, initialDescriptions = [], type, additionalPrompt, selectedDescription, setSelectedDescription, setItemId }) => {
  const [descriptionVariants, setDescriptionVariants] = useState<EnhancementDescriptionResponse[]>(initialDescriptions)

  const createJob = useCallback(async () => {
    const { data } = await productApi.createAiDescription({
      productId,
      additionalPrompt
    })
    return { jobId: data.jobId }
  }, [productId, additionalPrompt])

  const { isLoaded, isError, data, retry } = useSocketRequest<EnhancementDescriptionResponse>({
    enabled: type === 'create',
    createJob
  })

  useEffect(() => {
    if (data) {
      setDescriptionVariants([...descriptionVariants, data])
      setSelectedDescription(data)
      setItemId(data.enhancementId)
    }
  }, [data])

  return (
    <div className={styles.container}>
      <Typography.Title level={4} className={styles.title}>Описание:</Typography.Title>
      <div className={classNames(styles.contentWrapper, { [styles.contentWrapperWithData]: descriptionVariants.length > 0 && isLoaded })}>
        {!isLoaded && (
          <ThreeDot color='#FF7905' size='medium' text='' textColor='' />
        )}
        {isError && isLoaded && (
          <Result
            status='error'
            title='Ошибка при генерации описания'
            subTitle='Попробуйте еще раз'
            extra={[
              <OrangeButton onClick={retry}>
                Попробовать еще раз
              </OrangeButton>
            ]}
          />
        )}
        {!isError && isLoaded && selectedDescription && (
          <>
            <div className={styles.inputWrapper}>
              <Input.TextArea
                value={selectedDescription?.new_desc}
                onChange={(e) => {
                  setSelectedDescription({ ...selectedDescription, new_desc: e.target.value })
                  setDescriptionVariants(prev => prev.map(variant =>
                    variant._id === selectedDescription._id ? { ...variant, new_desc: e.target.value } : variant
                  ))
                }}
                placeholder='Описание'
                size='large'
                autoSize={{ minRows: 10, maxRows: 500 }}
                className={styles.textArea}
              />
              <AiBadge className={styles.aiBadge} />
            </div>
            <div className={styles.variantsButtons}>
              {descriptionVariants.map((variant, index) => (
                <VariantButton
                  text={`Вариант ${index + 1}`}
                  isActive={selectedDescription?._id === variant._id}
                  onClick={() => setSelectedDescription(variant)}
                  key={variant._id}
                />
              ))}
            </div>
            <RegenerateAiButton onClick={retry} size='large' style={{ marginTop: 'auto' }} />
          </>
        )}
      </div>
    </div>
  )
})